import type { MetDiagnosticsSection } from '@/versions/v1/schema/met-diagnostics-cause';

export type ChatCitation = { label: string; id?: string };

export type ChatMessagePart =
  | { type: 'markdown'; content: string }
  | { type: 'tool'; verb: string; preview: string; detail?: string }
  | { type: 'artifact'; title: string; kind: string }
  /** Computed at render time from the live `AdvisorySessionState` — never a
   * frozen string — so an in-session approval that moves the rule-set
   * version is reflected the moment it renders (review MUST-FIX M4). See
   * `rule-set-retrieval.ts` § pbRecoveryRetrieval and
   * `ChatMessages.tsx`'s `RuleSetRetrievalPart`. */
  | { type: 'rule-set-retrieval'; section: MetDiagnosticsSection };

export type ChatMessage = {
  id: number;
  who: 'user' | 'bot';
  time: string;
  body: string;
  citations?: ChatCitation[];
  parts?: ChatMessagePart[];
};

export type ChatThread = {
  id: string;
  title: string;
  pinned: boolean;
  updatedAt: number;
  messages: ChatMessage[];
  /** Present on project-scoped threads (see thread-seed.ts). */
  projectSlug?: string;
};

export type ThreadNoticeSeverity = 'info' | 'warning' | 'critical';

export type ThreadNotice = {
  id: string;
  kind: string;
  severity: ThreadNoticeSeverity;
  body: string;
  actionHint?: string;
};

export type ComposerStyleId = 'default' | 'concise' | 'formal';

export const COMPOSER_STYLE_LABELS: Record<ComposerStyleId, string> = {
  default: 'Default',
  concise: 'Concise',
  formal: 'Formal',
};

export const NEW_THREAD_ID = 'thread-new';

/** Messages kept visible after manual compact — shared by engine + plus-menu. */
export const TAIL_PRESERVE_MESSAGE_COUNT = 2;

export type PermissionMode = 'default' | 'bypassPermissions';

export type ModelProvider = 'anthropic' | 'google' | 'openai';

export type ModelOption = {
  id: string;
  label: string;
  shortLabel: string;
  description: string;
  provider: ModelProvider;
};

export const DEFAULT_MODEL_ID = 'claude-opus-4-8';

/** Composer model catalog — mirrors ai-foundation `src/lib/models.ts` MODELS. */
export const SEED_MODELS: ModelOption[] = [
  {
    id: 'claude-opus-4-8',
    label: 'Claude Opus 4.8',
    shortLabel: 'Opus 4.8',
    description: 'Most capable. Best for complex reasoning and long-running tasks.',
    provider: 'anthropic',
  },
  {
    id: 'claude-sonnet-4-6',
    label: 'Claude Sonnet 4.6',
    shortLabel: 'Sonnet 4.6',
    description: 'Balanced speed and intelligence. Good default.',
    provider: 'anthropic',
  },
  {
    id: 'claude-haiku-4-5',
    label: 'Claude Haiku 4.5',
    shortLabel: 'Haiku 4.5',
    description: 'Fastest and cheapest. Best for simple turns.',
    provider: 'anthropic',
  },
  {
    id: 'gemini-3.5-flash',
    label: 'Gemini 3.5 Flash',
    shortLabel: 'Gemini Flash',
    description: 'Fast Gemini model via Google ADK (feature-flagged).',
    provider: 'google',
  },
  {
    id: 'gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro',
    shortLabel: 'Gemini Pro',
    description: 'Capable Gemini model via Google ADK (feature-flagged).',
    provider: 'google',
  },
  {
    id: 'gemini-3.1-flash-lite',
    label: 'Gemini 3.1 Flash-Lite',
    shortLabel: 'Gemini Flash-Lite',
    description:
      'Most cost-efficient — high-volume agentic tasks and simple processing (via Google ADK, feature-flagged).',
    provider: 'google',
  },
];
